import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  ArrowLeft, Users, Plus, Trash2, Edit2, Crown, Loader2,
  CheckCircle2, AlertTriangle, XCircle, UserPlus, Briefcase, X, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { formatDateMMDDYYYY } from '../../utils/timeFormat';

const ClientTeams = () => {
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [creating, setCreating] = useState(false);
  const [bookingTeam, setBookingTeam] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [selectedShift, setSelectedShift] = useState('');
  const [availability, setAvailability] = useState(null);
  const [bookingInProgress, setBookingInProgress] = useState(false);
  const [showSavePrompt, setSavePrompt] = useState(null); // {shift_id, assigned_ids}

  useEffect(() => { fetchTeams(); }, []);

  const fetchTeams = async () => {
    try {
      const res = await api.get('/teams');
      setTeams(res.data.teams || []);
    } catch { toast.error('Failed to load teams'); }
    finally { setLoading(false); }
  };

  const createTeam = async () => {
    if (!newTeamName.trim()) return;
    setCreating(true);
    try {
      await api.post('/teams', { team_name: newTeamName.trim(), talent_ids: [] });
      toast.success('Team created!');
      setNewTeamName('');
      setShowCreate(false);
      fetchTeams();
    } catch { toast.error('Failed to create team'); }
    finally { setCreating(false); }
  };

  const deleteTeam = async (teamId, teamName) => {
    if (!window.confirm(`Delete "${teamName}"?`)) return;
    try {
      await api.delete(`/teams/${teamId}`);
      toast.success('Team deleted');
      fetchTeams();
    } catch { toast.error('Failed to delete'); }
  };

  const removeMember = async (teamId, talentId) => {
    try {
      await api.delete(`/teams/${teamId}/members/${talentId}`);
      toast.success('Removed from team');
      fetchTeams();
    } catch { toast.error('Failed to remove'); }
  };

  const startBooking = async (team) => {
    setBookingTeam(team);
    setSelectedShift('');
    setAvailability(null);
    try {
      const res = await api.get('/client/shifts?status=open');
      setShifts(res.data?.shifts || res.data || []);
    } catch {
      setShifts([]);
    }
  };

  const checkAvailability = async (shiftId) => {
    setSelectedShift(shiftId);
    try {
      const res = await api.get(`/teams/${bookingTeam.id}/availability?shift_id=${shiftId}`);
      setAvailability(res.data);
    } catch {
      toast.error('Failed to check availability');
    }
  };

  const bookAvailable = async () => {
    if (!availability || !selectedShift) return;
    const availableIds = availability.members.filter(m => m.status === 'available').map(m => m.talent_id);
    if (availableIds.length === 0) {
      toast.error('No available members to book');
      return;
    }
    setBookingInProgress(true);
    try {
      const res = await api.post(`/teams/${bookingTeam.id}/book`, {
        shift_id: selectedShift,
        talent_ids: availableIds,
      });
      const skippedCount = availability.total_members - res.data.assigned;
      toast.success(`${res.data.assigned} members assigned!`);
      
      // Show save-as-team prompt if this was a good booking
      if (res.data.assigned >= 2) {
        setSavePrompt({ assigned: res.data.assigned, skipped: skippedCount, shift_id: selectedShift });
      }
      
      setBookingTeam(null);
      setAvailability(null);
    } catch {
      toast.error('Booking failed');
    } finally {
      setBookingInProgress(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="client-teams-page">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/client/home')}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-lg font-semibold text-gray-900">My Teams</h1>
          </div>
          <Button size="sm" onClick={() => setShowCreate(true)} data-testid="create-team-btn">
            <Plus className="w-4 h-4 mr-1.5" />Create Team
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Create Team Inline */}
        {showCreate && (
          <Card className="border-violet-200 bg-violet-50/50" data-testid="create-team-form">
            <CardContent className="p-4">
              <p className="text-sm font-medium text-gray-700 mb-2">New Team Name</p>
              <div className="flex gap-2">
                <Input
                  value={newTeamName}
                  onChange={(e) => setNewTeamName(e.target.value)}
                  placeholder="e.g. Atlanta Brand Team"
                  data-testid="team-name-input"
                  onKeyDown={(e) => e.key === 'Enter' && createTeam()}
                />
                <Button onClick={createTeam} disabled={creating || !newTeamName.trim()} data-testid="save-team-btn">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
                </Button>
                <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Teams List */}
        {teams.length === 0 && !showCreate ? (
          <div className="text-center py-16" data-testid="empty-teams">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-600 mb-2">No teams yet</h2>
            <p className="text-sm text-gray-500 mb-4">Save groups of talent for quick rebooking</p>
            <Button onClick={() => setShowCreate(true)}>
              <Plus className="w-4 h-4 mr-1.5" />Create Your First Team
            </Button>
          </div>
        ) : (
          teams.map(team => (
            <Card key={team.id} data-testid={`team-card-${team.id}`}>
              <CardHeader className="py-3 px-5 flex flex-row items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-violet-600" />
                  <CardTitle className="text-sm font-semibold">{team.team_name}</CardTitle>
                  <Badge className="bg-gray-100 text-gray-600 text-[10px]">{team.member_count} members</Badge>
                  {team.performance?.avg_reliability > 0 && (
                    <Badge className="bg-emerald-50 text-emerald-700 text-[10px]">
                      Reliability {team.performance.avg_reliability}
                    </Badge>
                  )}
                  {team.performance?.shifts_together > 0 && (
                    <Badge className="bg-blue-50 text-blue-700 text-[10px]">
                      {team.performance.shifts_together} shifts together
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="default" size="sm"
                    className="text-xs bg-violet-600 hover:bg-violet-700"
                    onClick={() => startBooking(team)}
                    data-testid={`book-team-${team.id}`}
                  >
                    <Briefcase className="w-3.5 h-3.5 mr-1" />Book Team
                  </Button>
                  <Button
                    variant="ghost" size="sm"
                    className="text-gray-400 hover:text-violet-600"
                    onClick={() => navigate(`/client/discover`)}
                    title="Add talent"
                  >
                    <UserPlus className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost" size="sm"
                    className="text-gray-400 hover:text-red-500"
                    onClick={() => deleteTeam(team.id, team.team_name)}
                    data-testid={`delete-team-${team.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="px-5 pb-5">
                {team.members.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">No members yet — add talent from Discovery</p>
                ) : (
                  <div className="space-y-2">
                    {team.members.map(member => (
                      <div key={member.talent_id} className="flex items-center justify-between p-2.5 border rounded-lg hover:bg-gray-50">
                        <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate(`/client/discover/${member.talent_id}`)}>
                          {member.photo_url ? (
                            <img src={member.photo_url} alt={member.name} className="w-9 h-9 rounded-full object-cover" />
                          ) : (
                            <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-xs font-bold">
                              {member.name?.charAt(0)}
                            </div>
                          )}
                          <div>
                            <div className="flex items-center gap-1.5">
                              <p className="text-sm font-medium text-gray-800">{member.name}</p>
                              {member.is_premium && <Crown className="w-3 h-3 text-amber-500" />}
                            </div>
                            <p className="text-[10px] text-gray-400">{member.city}, {member.state}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {member.rating > 0 && (
                            <span className="text-xs text-amber-600">{member.rating.toFixed(1)}</span>
                          )}
                          <Button
                            variant="ghost" size="sm"
                            className="text-gray-300 hover:text-red-500 p-1"
                            onClick={() => removeMember(team.id, member.talent_id)}
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Save as Team Prompt — after successful booking */}
      {showSavePrompt && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-white border border-violet-200 shadow-xl rounded-xl p-4 max-w-sm w-full mx-4" data-testid="save-team-prompt">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">{showSavePrompt.assigned} members assigned!</p>
              {showSavePrompt.skipped > 0 && (
                <p className="text-xs text-amber-600 mt-0.5">{showSavePrompt.skipped} unavailable — need to fill remaining?</p>
              )}
            </div>
            <button onClick={() => setSavePrompt(null)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex gap-2 mt-3">
            {showSavePrompt.skipped > 0 && (
              <Button
                variant="outline" size="sm" className="flex-1 text-xs border-cyan-300 text-cyan-700"
                onClick={() => { setSavePrompt(null); navigate('/client/discover'); }}
                data-testid="auto-fill-btn"
              >
                <Zap className="w-3.5 h-3.5 mr-1" />Fill Remaining
              </Button>
            )}
            <Button
              variant="ghost" size="sm" className="text-xs text-gray-500"
              onClick={() => setSavePrompt(null)}
            >
              Dismiss
            </Button>
          </div>
        </div>
      )}

      {/* Book Team Modal */}
      {bookingTeam && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setBookingTeam(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()} data-testid="book-team-modal">
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h3 className="font-semibold text-gray-900 text-sm">Book {bookingTeam.team_name}</h3>
                <p className="text-[10px] text-gray-400">{bookingTeam.member_count} members</p>
              </div>
              <button onClick={() => setBookingTeam(null)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
            </div>

            <div className="p-4 space-y-4">
              {/* Shift Selection */}
              <div>
                <p className="text-xs font-medium text-gray-600 mb-1.5">Select Shift</p>
                {shifts.length > 0 ? (
                  <select
                    value={selectedShift}
                    onChange={(e) => checkAvailability(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    data-testid="shift-select"
                  >
                    <option value="">Choose a shift...</option>
                    {shifts.map(s => (
                      <option key={s.id} value={s.id}>{s.title || s.role_name || s.venue_name || 'Shift'} — {s.shift_date ? formatDateMMDDYYYY(s.shift_date) : 'TBD'}</option>
                    ))}
                  </select>
                ) : (
                  <p className="text-sm text-gray-400">No open shifts. Create one first.</p>
                )}
              </div>

              {/* Availability Results */}
              {availability && (
                <div data-testid="availability-results">
                  <div className="flex items-center gap-2 mb-2">
                    <p className="text-xs font-medium text-gray-600">Availability</p>
                    <Badge className="bg-green-100 text-green-700 text-[10px]">{availability.available}/{availability.total_members} available</Badge>
                  </div>
                  <div className="space-y-1.5 max-h-48 overflow-y-auto">
                    {availability.members.map(m => (
                      <div key={m.talent_id} className="flex items-center justify-between p-2 border rounded-lg text-sm">
                        <div className="flex items-center gap-2">
                          {m.photo_url ? (
                            <img src={m.photo_url} alt="" className="w-7 h-7 rounded-full object-cover" />
                          ) : (
                            <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-500">{m.name?.charAt(0)}</div>
                          )}
                          <span className="text-gray-800">{m.name}</span>
                        </div>
                        {m.status === 'available' ? (
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                        ) : m.status === 'conflict' ? (
                          <AlertTriangle className="w-4 h-4 text-amber-500" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-400" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Book Action */}
            {availability && availability.available > 0 && (
              <div className="p-4 border-t bg-gray-50">
                <Button
                  className="w-full bg-violet-600 hover:bg-violet-700"
                  onClick={bookAvailable}
                  disabled={bookingInProgress}
                  data-testid="assign-available-btn"
                >
                  {bookingInProgress ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Briefcase className="w-4 h-4 mr-2" />}
                  Assign {availability.available} Available Members
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientTeams;
